import { REPEAT_MODE } from 'features/schedulingEngine/constants/repeatTypes'
import { hasEngineScheduleDeploy } from '../utils/engineScheduleProjection'
import { getAfterNOccurrencesEndRuleCount } from './engine/engineEndRules'
import { parseSimpleRrule } from './engine/rruleExpansion'

const endRuleOccurrenceCount = schedule => getAfterNOccurrencesEndRuleCount(schedule)

const repeatOccurrenceCount = schedule => {
  const repeat = schedule?.repeat
  const fromEndRules = endRuleOccurrenceCount(schedule)
  let fromRepeat = null
  if (repeat) {
    const raw = repeat.recurrences ?? repeat.count
    if (raw != null && raw > 0) fromRepeat = Math.floor(Number(raw))
    else if (repeat.mode === 'rrule') {
      const parsed = parseSimpleRrule(repeat.rrule)
      if (parsed.count != null) fromRepeat = parsed.count
    }
  }
  if (fromRepeat != null && fromEndRules != null) return Math.min(fromRepeat, fromEndRules)
  return fromRepeat ?? fromEndRules
}

const repeatDayStep = schedule => {
  const repeat = schedule?.repeat
  if (!repeat) return 1
  if (repeat.mode === REPEAT_MODE.everyNDays || repeat.mode === 'every_n_days') {
    const interval = repeat.intervalDays ?? repeat.interval_days ?? 1
    return Math.max(1, Math.floor(Number(interval)) || 1)
  }
  if (repeat.mode === 'rrule') {
    const parsed = parseSimpleRrule(repeat.rrule)
    if (parsed.freq === 'WEEKLY') return Math.max(1, parsed.interval) * 7
    return Math.max(1, parsed.interval)
  }
  const legacy = schedule?.recurring
  if (legacy?.scheme === 'n-days') return Math.max(1, Math.floor(Number(legacy.interval)) || 1)
  return 1
}

const expireFirstLoginLastDay = (firstStudyDay, schedule) => {
  const interval = schedule?.expire?.first_login?.interval
  if (interval == null) return null
  const n = Math.floor(Number(interval))
  if (!Number.isFinite(n) || n < 1) return null
  const deployInterval = schedule?.deploy?.first_login?.interval
  const deployOffset =
    deployInterval != null && Number.isFinite(Number(deployInterval)) ? Math.floor(Number(deployInterval)) : 0
  return Math.max(firstStudyDay, firstStudyDay + n - deployOffset - 1)
}

/**
 * Last study day this schedule would surface without capping to study duration.
 * @param {number} firstStudyDay
 * @param {object} schedule
 * @returns {number|null}
 */
export const getScheduleIntendedLastStudyDay = (firstStudyDay, schedule) => {
  const first = Math.max(1, Math.floor(Number(firstStudyDay)))
  if (!schedule || typeof schedule !== 'object') return first

  const isEngine = hasEngineScheduleDeploy(schedule)
  const repeat = schedule.repeat
  const legacy = schedule.recurring
  const occurrences = repeatOccurrenceCount(schedule)
  const step = repeatDayStep(schedule)

  if (occurrences != null && occurrences > 0) {
    return first + (occurrences - 1) * step
  }

  if (isEngine) {
    if (legacy?.scheme === 'persistent' || repeat?.mode === 'persistent') return null
    if (!repeat || repeat.mode === REPEAT_MODE.doesNotRepeat || repeat.mode === 'does_not_repeat') {
      return first
    }
    return null
  }

  if (legacy?.recurrences != null && legacy.recurrences > 0) {
    const legacyStep =
      legacy.scheme === 'n-months'
        ? Math.max(1, Math.floor((Number(legacy.interval) || 1) * 30))
        : legacy.scheme === 'n-hours'
          ? Math.max(1, Math.floor((Number(legacy.interval) || 0) / 24))
          : Math.max(1, Math.floor(Number(legacy.interval)) || 1)
    return first + (Math.floor(Number(legacy.recurrences)) - 1) * legacyStep
  }

  const expireLast = expireFirstLoginLastDay(first, schedule)
  if (expireLast != null) return expireLast

  if (legacy?.scheme === 'persistent' || repeat?.mode === 'persistent') {
    return expireLast
  }

  if (!repeat || repeat.mode === REPEAT_MODE.doesNotRepeat || repeat.mode === 'does_not_repeat') {
    return first
  }

  return null
}
